'use client';

import React, { useState, useCallback } from 'react';
import { Button, Card, CardContent, TextField, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useRegister, useSetCurrentBusinessUnit, type BusinessUnitDto } from '@/hooks/api/useAuth';
import { useNotification } from '@/contexts/NotificationContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { BusinessUnitPickerDialog } from './BusinessUnitPickerDialog';

interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
}

const RegisterSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Confirm password is required'),
});

const Register: React.FC = () => {
  const registerMutation = useRegister();
  const setBuMutation = useSetCurrentBusinessUnit();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [buPickerOpen, setBuPickerOpen] = useState(false);
  const [buChoices, setBuChoices] = useState<BusinessUnitDto[]>([]);

    const handleLogin = () => {
        window.localStorage.setItem('authComponentMode', 'login');
        window.location.reload();
    }

    console.log("site key?", import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY)

  const handleRegister = useCallback((values: RegisterFormValues) => {
    if (!captchaValue) {
      showError("Please complete the reCAPTCHA verification.");
      return;
    }

    registerMutation.mutate(
      {
        email: values.email.trim(),
        password: values.password.trim(),
        captchaToken: captchaValue,
      },
      {
        onSuccess: (data) => {
          if (data.businessUnits && data.businessUnits.length > 1) {
            setBuChoices(data.businessUnits);
            setBuPickerOpen(true);
          }
        },
        onError: (error: any) => {
          console.error("Registration error", error);
          showError(error.message || 'Registration failed. Please try again.');
        },
      }
    );
  }, [registerMutation, captchaValue, showError]);

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handlePickBu = (id: number) => {
    setBuMutation.mutate(id, {
      onSuccess: () => {
        setBuPickerOpen(false);
        navigate('/app/dashboard');
      },
      onError: (e: any) => {
        showError(e?.message || 'Could not set business unit');
      },
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center',
      }}
    >
      <Card style={{ width: 400 }}>
        <CardContent>
          <Typography color="textSecondary" gutterBottom>
            Register
          </Typography>
          <Formik<RegisterFormValues>
            initialValues={{ email: '', password: '', confirmPassword: '' }}
            validationSchema={RegisterSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleRegister(values);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <div style={{ marginTop: 16 }}>
                  <Field
                    name="email"
                    as={TextField}
                    label="Email"
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                  <ErrorMessage name="email" component="div" />
                </div>
                <div style={{ marginTop: 16 }}>
                  <Field
                    name="password"
                    as={TextField}
                    label="Password"
                    size="small"
                    fullWidth
                    variant="outlined"
                    type="password"
                    InputLabelProps={{ shrink: true }}
                  />
                  <ErrorMessage name="password" component="div" />
                </div>
                <div style={{ marginTop: 16 }}>
                  <Field
                    name="confirmPassword"
                    as={TextField}
                    label="Confirm Password"
                    size="small"
                    fullWidth
                    variant="outlined"
                    type="password"
                    InputLabelProps={{ shrink: true }}
                  />
                  <ErrorMessage name="confirmPassword" component="div" />
                </div>
                
                {/* reCAPTCHA */}
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA
                    sitekey={import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || ''}
                    onChange={handleCaptchaChange}
                  />
                </div>

                <div style={{ marginTop: 16 }}>
                  <Button type="submit" variant="outlined" disabled={isSubmitting || !captchaValue || registerMutation.isPending}>
                    Register
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          <div style={{ marginTop: 16 }}>
          Already have an account?
          <br />
          <Button style={{ marginTop: 16 }} onClick={() => handleLogin()}>
            Login
          </Button>
        </div>
        </CardContent>
      </Card>
      <BusinessUnitPickerDialog
        open={buPickerOpen}
        businessUnits={buChoices}
        onSelect={handlePickBu}
        isLoading={setBuMutation.isPending}
      />
    </div>
  );
};

export default Register;
